import { createMachine, assign, InterpreterFrom, sendParent } from "xstate-4";
import { useActor } from "@xstate-4/vue";
import { xlog } from "vue-app/utils/xstate";
import axios from "vue-app/utils/axios";
import { machineInboxNewMessage } from "vue-app/components/Inbox/machines/machineInboxNewMessage";
import { Conversation, Integrations } from "vue-app/pages/machines/machinePageInbox";
import { gtagSend } from "vue-app/plugins/edroneGtag";
import { getAutoresponderData, editAutoresponderData } from "vue-app/api/inbox/autoresponder.ts";
import { getWorkingHours } from "vue-app/api/account/working-hours.ts";
import { getChatConfig } from "vue-app/api/onsites/chat";
import { DTOAutoresponderConfig, DTOWorkingHours } from "vue-app/api/dto";
import { useRouter } from "vue-app/composables/useRouter";
import { useRoute } from "vue-router";

export enum InboxStatus {
  // "ALL",
  "OPEN" = 1,
  "SNOOZED",
  "CLOSED",
  "AI_AGENT" = 100, // Special status for AI Agent tab - not a real conversation status
}

export enum InboxAssignment {
  "ALL",
  "YOU",
  "UNASSIGNED",
}

export enum InboxSource {
  "ALL",
  "EMAIL",
  "CHAT",
  "FACEBOOK" = 4,
  "WHATSAPP" = 8,
  "ALLEGRO" = 16,
  "ALLEGRO_DISPUTE" = 32,
  "ALLEGRO_CLAIM" = 64,
  "AI_AGENT" = 128,
}

export enum InboxRead {
  "ALL",
  "READ",
  "UNREAD",
}

export enum InboxExpiration {
  "ABOUT_TO_EXPIRE",
  "EXPIRED",
}

export type ConversationFilters = {
  assignment: InboxAssignment[];
  source: InboxSource[];
  search: string;
  read: InboxRead[];
  expiration: InboxExpiration[];
};

export type MachineInboxConversationsContext = {
  integrations: Integrations | null;
  conversations: Conversation[];
  selectedStatus: InboxStatus;
  filters: ConversationFilters;
  canLoadMore: boolean;
  areFiltersActive: boolean;
  inboxSendWithEnter: boolean;
  inboxNotification: boolean;
  autoresponder: Partial<DTOAutoresponderConfig>;
  isAutoresponderDataFetched: boolean;
  workingHours: { days: DTOWorkingHours[] } | null;
  chatConfig: Array<{ isActive: boolean }>;
  showTestConversations: boolean;
};

export const machineInboxConversations = createMachine(
  {
    id: "InboxConversations",
    tsTypes: {} as import("./machineInboxConversations.typegen").Typegen0,
    schema: {
      events: {} as
        | { type: "RETRY_CONVERSATIONS" }
        | { type: "LOAD_MORE" }
        | { type: "UPDATE_CONVERSATION"; conversationData: Conversation }
        | { type: "CLOSE_NEW_MESSAGE_MODAL" }
        | { type: "OPEN_NEW_MESSAGE_MODAL" }
        | { type: "OPEN_INBOX_SETTINGS" }
        | { type: "OPEN_CHANNELS_DRAWER" }
        | { type: "CLOSE_CHANNELS_DRAWER" }
        | { type: "OPEN_AUTORESPONDER_SETTINGS_DRAWER" }
        | { type: "CLOSE_AUTORESPONDER_SETTINGS_DRAWER" }
        | { type: "MARK_ALL_AS_READ" }
        | { type: "CLOSE_INBOX_SETTINGS" }
        | {
            type: "CHANGE_INBOX_SETTINGS";
            inboxSendWithEnter: boolean;
            inboxNotification: boolean;
          }
        | { type: "SELECT_CONVERSATION"; conversationData: Conversation }
        | { type: "APPLY_FILTERS"; filters: ConversationFilters }
        | { type: "CHANGE_STATUS"; status: InboxStatus }
        | { type: "TOGGLE_SHOW_TEST_CONVERSATIONS" }
        | { type: "TOGGLE_AUTORESPONDER" }
        | { type: "CHANGE_AUTORESPONDER_MESSAGE"; value: string }
        | { type: "TOGGLE_SEND_FROM"; channel: "chat" | "email" | "whatsapp" | "messenger" | "allegro" }
        | { type: "TOGGLE_MESSAGE_OUTSIDE_OF_WORKING_HOURS" }
        | { type: "CHANGE_MESSAGE_OUTSIDE_OF_WORKING_HOURS"; value: string }
        | { type: "UPDATE_WORKING_HOURS"; workingHours: { days: DTOWorkingHours[] } },
      context: {} as MachineInboxConversationsContext,
      services: {} as {
        fetchingConversations: {
          data: Conversation[];
        };
        changeInboxSettings: {
          data: { inboxSendWithEnter: boolean; inboxNotification: boolean };
        };
        fetchAutoresponderData: {
          data: {
            autoresponderData: Partial<DTOAutoresponderConfig>;
            workingHours: { days: DTOWorkingHours[] };
            chatConfig: { chatConfig: Array<{ isActive: boolean }>; status: string };
          };
        };
        editAutoresponderData: {
          data: { autoresponderData: Partial<DTOAutoresponderConfig> };
        };
      },
    },
    context: {
      integrations: null,
      conversations: [],
      selectedStatus: InboxStatus.OPEN,
      filters: {
        assignment: [],
        source: [],
        read: [],
        search: "",
        expiration: [],
      },
      canLoadMore: false,
      areFiltersActive: false,
      inboxSendWithEnter: window.EDRONE_CONTEXT.user.inboxSendWithEnter,
      inboxNotification: window.EDRONE_CONTEXT.user.inboxNotification,
      autoresponder: {},
      isAutoresponderDataFetched: false,
      workingHours: null,
      chatConfig: [],
      showTestConversations: true,
    },
    states: {
      autoresponderSettingsDrawer: {
        tags: ["autoresponderSettingsDrawer"],
        initial: "closed",

        states: {
          closed: {
            tags: "drawerAutoResponderClosed",
            entry: "removeAutoresponderQueryParam",
            on: {
              OPEN_AUTORESPONDER_SETTINGS_DRAWER: {
                target: "open",
              },
            },
          },

          open: {
            tags: ["autoresponderSettingsDrawerOpen"],

            states: {
              transient: {
                always: [
                  {
                    target: "idle",
                    cond: (context) => context.isAutoresponderDataFetched,
                  },
                  {
                    target: "fetching",
                  },
                ],
              },

              fetching: {
                tags: "fetchingAutoresponderData",
                invoke: {
                  src: "fetchAutoresponderData",
                  id: "fetchAutoresponderData",
                  onDone: {
                    target: "idle",
                    actions: [
                      "assignAutoresponderData",

                      "assignIsAutoresponderDataFetched",
                      "assignWorkingHours",
                      "assignChatConfig",
                    ],
                  },
                  onError: {
                    target: "idle",
                    actions: ["toastError"],
                  },
                },
              },

              idle: {
                on: {
                  TOGGLE_AUTORESPONDER: {
                    actions: "assignAutoresponderEnabled",
                    target: ".editing",
                    internal: false,
                  },

                  UPDATE_WORKING_HOURS: {
                    target: ".debouncing",
                    actions: "assignWorkingHoursAfterChange",
                    internal: false,
                  },

                  CHANGE_AUTORESPONDER_MESSAGE: {
                    actions: "assignAutoresponderMessage",
                    target: ".debouncing",
                    internal: false,
                    cond: (context) => !context.autoresponder.isEnabled,
                  },

                  CHANGE_MESSAGE_OUTSIDE_OF_WORKING_HOURS: {
                    target: ".debouncing",
                    internal: false,
                    actions: "assignAutoresponderOutsideOfWorkingHoursMessage",
                    cond: (context) => !context.autoresponder.isOutsideWorkingHoursMessageEnabled,
                  },

                  TOGGLE_MESSAGE_OUTSIDE_OF_WORKING_HOURS: {
                    target: ".editing",
                    internal: false,
                    actions: "assignAutoresponderOutsideOfWorkingHoursMessageEnabled",
                  },

                  CLOSE_AUTORESPONDER_SETTINGS_DRAWER: {
                    target: "#InboxConversations.autoresponderSettingsDrawer.closed",
                    actions: "fireAndForgetEditAutoresponderData",
                  },

                  TOGGLE_SEND_FROM: {
                    actions: "assignAutoresponderIsSentFrom",
                    target: ".editing",
                  },
                },

                states: {
                  editing: {
                    tags: "editingAutoresponderData",

                    states: {
                      pending: {
                        invoke: {
                          src: "editAutoresponderData",
                          id: "editAutoresponderData",

                          onDone: {
                            target: "done",
                            actions: "toastSuccess",
                          },

                          onError: {
                            target: "done",
                            actions: "toastError",
                          },
                        },
                      },
                      done: {},
                    },

                    initial: "pending",
                  },

                  debouncing: {
                    after: {
                      "500": "editing",
                    },
                  },
                },
              },
            },

            initial: "transient",
            entry: "addAutoresponderQueryParam",
          },
        },
      },

      inbox: {
        initial: "loadingConversations",

        states: {
          markingAllAsRead: {
            tags: ["markingAllAsRead"],
            invoke: {
              id: "markAllAsRead",
              src: "markAllAsRead",
              onDone: {
                target: "loadingConversations",
                actions: [() => window.pubSub.publish("menu.badges.refresh")],
              },
              onError: {
                target: "idle",
              },
            },
          },
          loadingConversations: {
            tags: ["loadingConversations"],
            entry: ["assignConversations"],
            invoke: {
              src: "fetchingConversations",
              id: "fetchingConversations",
              onDone: [
                {
                  target: "idle",
                  actions: ["assignConversations", "assignCanLoadMore"],
                },
              ],
              onError: [
                {
                  target: "errorGettingConversations",
                },
              ],
            },
          },

          errorGettingConversations: {
            tags: ["errorGettingConversations"],
            on: {
              RETRY_CONVERSATIONS: {
                target: "loadingConversations",
              },
              CHANGE_STATUS: {
                actions: ["assignStatus"],
                target: "loadingConversations",
              },
              TOGGLE_SHOW_TEST_CONVERSATIONS: {
                actions: ["toggleShowTestConversations"],
                target: "loadingConversations",
              },
              APPLY_FILTERS: [
                {
                  actions: ["showIsDemoToast"],
                  cond: "isDemo",
                },
                {
                  actions: ["assignFilters", "assignAreFiltersActive"],
                  target: "loadingConversations",
                },
              ],
              OPEN_NEW_MESSAGE_MODAL: {
                target: "creatingNewMessageModal",
                description: "Open modal",
              },
            },
          },

          creatingNewMessageModal: {
            tags: ["creatingNewMessageModal"],
            initial: "creatingMessage",
            states: {
              creatingMessage: {
                tags: ["creatingMessage"],
                invoke: {
                  src: "createMessage",
                  id: "createMessage",
                },
              },
            },
            on: {
              CLOSE_NEW_MESSAGE_MODAL: {
                target: "idle",
              },
              UPDATE_CONVERSATION: [
                {
                  actions: ["updateConversation"],
                  cond: "isUpdatedConversationOnListAndMatchesFilters",
                },
                {
                  actions: ["removeConversation"],
                  cond: "isUpdatedConversationOnListButDoesntMatchFilters",
                },
                {
                  actions: ["addConversation"],
                  cond: "isUpdatedConversationNotOnListButMatchesFilters",
                },
              ],
            },
          },

          idle: {
            tags: ["idle"],

            on: {
              MARK_ALL_AS_READ: {
                target: "markingAllAsRead",
              },
              CHANGE_STATUS: {
                actions: ["assignStatus", "gtag"],
                target: "loadingConversations",
              },

              TOGGLE_SHOW_TEST_CONVERSATIONS: {
                actions: ["toggleShowTestConversations"],
                target: "loadingConversations",
              },

              APPLY_FILTERS: [
                {
                  actions: ["showIsDemoToast"],
                  cond: "isDemo",
                },
                {
                  actions: ["assignFilters", "assignAreFiltersActive", "gtag"],
                  target: "loadingConversations",
                },
              ],

              OPEN_NEW_MESSAGE_MODAL: {
                target: "creatingNewMessageModal",
                actions: ["gtag"],
              },

              OPEN_INBOX_SETTINGS: {
                target: "settingsModal",
                actions: ["gtag"],
              },

              LOAD_MORE: {
                target: "loadingConversations",
                cond: "canLoadMore",
                actions: ["gtag"],
              },

              UPDATE_CONVERSATION: [
                {
                  actions: ["updateConversation"],
                  cond: "isUpdatedConversationOnListAndMatchesFilters",
                },
                {
                  actions: ["removeConversation"],
                  cond: "isUpdatedConversationOnListButDoesntMatchFilters",
                },
                {
                  actions: ["addConversation"],
                  cond: "isUpdatedConversationNotOnListButMatchesFilters",
                },
              ],
            },
          },

          changeInboxSettings: {
            invoke: {
              src: "changeInboxSettings",
              id: "changeInboxSettings",
              onDone: [
                {
                  target: "idle",
                  actions: ["showSettingsChangeConfirmation", "assignInboxSendWithEnter", "assignInboxNotification"],
                },
              ],
              onError: [
                {
                  target: "idle",
                  actions: ["showSettingsChangeConfirmation"],
                },
              ],
            },
          },

          settingsModal: {
            tags: ["settingsModal"],
            on: {
              CLOSE_INBOX_SETTINGS: {
                target: "idle",
              },
              CHANGE_INBOX_SETTINGS: {
                target: "changeInboxSettings",
              },
              UPDATE_CONVERSATION: [
                {
                  actions: ["updateConversation"],
                  cond: "isUpdatedConversationOnListAndMatchesFilters",
                },
                {
                  actions: ["removeConversation"],
                  cond: "isUpdatedConversationOnListButDoesntMatchFilters",
                },
                {
                  actions: ["addConversation"],
                  cond: "isUpdatedConversationNotOnListButMatchesFilters",
                },
              ],
            },
          },
        },
      },

      channels: {
        states: {
          closed: {
            on: {
              OPEN_CHANNELS_DRAWER: "open",
            },
          },

          open: {
            tags: ["channelsDrawerOpen"],
            on: {
              CLOSE_CHANNELS_DRAWER: "closed",
            },
          },
        },

        initial: "closed",
      },
    },
    on: {
      SELECT_CONVERSATION: {
        actions: [
          sendParent((context, event) => {
            xlog({
              label: "selectConversation",
              type: "action",
              context,
              event,
            });
            return {
              type: "SELECT_CONVERSATION",
              conversationData: event.conversationData,
            };
          }),
        ],
      },
    },
    predictableActionArguments: true,
    preserveActionOrder: true,
    // Fetch autoresponder data once when machine starts (for initial counts)
    invoke: [
      {
        id: "initialFetchAutoresponderData",
        src: "fetchAutoresponderData",
        onDone: {
          actions: [
            "assignAutoresponderData",
            "assignIsAutoresponderDataFetched",
            "assignWorkingHours",
            "assignChatConfig",
          ],
        },
        onError: {
          actions: ["toastError"],
        },
      },
    ],
    type: "parallel",
  },
  {
    actions: {
      addAutoresponderQueryParam: (context, event) => {
        xlog({ label: "addAutoresponderQueryParam", type: "action", context, event });
        const router = useRouter();
        router?.push({ query: { autoresponder: "true" } });
      },
      removeAutoresponderQueryParam: (context, event) => {
        xlog({ label: "removeAutoresponderQueryParam", type: "action", context, event });
        const router = useRouter();
        const route = useRoute();
        if (route?.query.allegro_email) {
          router?.replace({ query: {} });
        }
      },
      fireAndForgetEditAutoresponderData: (context) => {
        xlog({ label: "fireAndForgetEditAutoresponderData", type: "action" });
        editAutoresponderData(context.autoresponder);
      },
      assignConversations: assign({
        conversations: (context, event) => {
          xlog({ label: "assignConversations", type: "action", context, event });
          if (event.type === "LOAD_MORE") {
            return [...context.conversations];
          }
          if (
            event.type === "RETRY_CONVERSATIONS" ||
            event.type === "CHANGE_STATUS" ||
            event.type === "APPLY_FILTERS" ||
            event.type === "TOGGLE_SHOW_TEST_CONVERSATIONS" ||
            event.type === "xstate.init" ||
            event.type === "done.invoke.markAllAsRead"
          ) {
            return [];
          }
          return event.data || [];
        },
      }),
      updateConversation: assign({
        conversations: (context, event) => {
          xlog({ label: "updateConversation", type: "action", context, event });
          const index = context.conversations.findIndex(
            (conversation) => conversation.inboxConversationId === event.conversationData.inboxConversationId
          );
          if (index === -1) {
            return context.conversations;
          } else {
            const updatedConversations = [...context.conversations];
            updatedConversations[index] = event.conversationData;
            return sortConversationsByLastInboxMessageId(updatedConversations);
          }
        },
      }),
      removeConversation: assign({
        conversations: (context, event) => {
          xlog({ label: "removeConversation", type: "action", context, event });
          return context.conversations.filter(
            (conversation) => conversation.inboxConversationId !== event.conversationData.inboxConversationId
          );
        },
      }),
      addConversation: assign({
        conversations: (context, event) => {
          xlog({ label: "addConversation", type: "action", context, event });
          return sortConversationsByLastInboxMessageId([event.conversationData, ...context.conversations]);
        },
      }),
      assignStatus: assign({
        selectedStatus: (context, event) => {
          xlog({ label: "assignStatus", type: "action", context, event });
          return event.status;
        },
      }),
      toggleShowTestConversations: assign({
        showTestConversations: (context) => {
          xlog({ label: "toggleShowTestConversations", type: "action", context });
          return !context.showTestConversations;
        },
      }),
      assignFilters: assign({
        filters: (context, event) => {
          xlog({ label: "assignFilters", type: "action", context, event });
          return event.filters;
        },
      }),
      assignCanLoadMore: assign({
        canLoadMore: (context, event) => {
          xlog({ label: "assignCanLoadMore", type: "action", context, event });
          return !(event.data.length % 50);
        },
      }),
      assignAreFiltersActive: assign({
        areFiltersActive: (context, event) => {
          xlog({ label: "assignAreFiltersActive", type: "action", context, event });
          return (
            !!event.filters.search ||
            !!event.filters.read.length ||
            !!event.filters.assignment.length ||
            !!event.filters.source.length
          );
        },
      }),
      assignInboxSendWithEnter: assign({
        inboxSendWithEnter: (context, event) => {
          xlog({ label: "assignInboxSendWithEnter", type: "action", context, event });
          window.EDRONE_CONTEXT.user.inboxSendWithEnter = event.data.inboxSendWithEnter;
          return event.data.inboxSendWithEnter;
        },
      }),
      assignInboxNotification: assign({
        inboxNotification: (context, event) => {
          xlog({ label: "assignInboxNotification", type: "action", context, event });
          window.EDRONE_CONTEXT.user.inboxNotification = event.data.inboxNotification;
          return event.data.inboxNotification;
        },
      }),
      showSettingsChangeConfirmation: (context, event) => {
        xlog({ label: "showSettingsChangeConfirmation", type: "action", context, event });
        if (event.type === "done.invoke.changeInboxSettings") {
          window.showToast(window._trans._inbox.inboxConversations.settings.notifications.saveSuccess, "success");
        } else {
          window.showToast(window._trans._inbox.inboxConversations.settings.notifications.saveError, "error");
        }
      },
      assignAutoresponderData: assign({
        autoresponder: (context, event) => {
          xlog({ label: "assignAutoresponderData", type: "action", context, event });
          return event.data.autoresponderData;
        },
      }),

      assignIsAutoresponderDataFetched: assign({
        isAutoresponderDataFetched: (context, event) => {
          xlog({ label: "assignIsAutoresponderDataFetched", type: "action", context, event });
          return true;
        },
      }),
      assignAutoresponderEnabled: assign({
        autoresponder: (context, event) => {
          xlog({ label: "assignAutoresponderEnabled", type: "action", context, event });
          return {
            ...context.autoresponder,
            isEnabled: !context.autoresponder.isEnabled,
            isOutsideWorkingHoursMessageEnabled: context.autoresponder.isEnabled
              ? false
              : context.autoresponder.isOutsideWorkingHoursMessageEnabled,
            isSentFromEmail: !!(context.integrations?.email && !context.autoresponder.isEnabled),
            isSentFromChat: !!(context.chatConfig?.length && !context.autoresponder.isEnabled),
            isSentFromWhatsapp: !!(context.integrations?.whatsapp && !context.autoresponder.isEnabled),
            isSentFromMessenger: !!(context.integrations?.facebook && !context.autoresponder.isEnabled),
          };
        },
      }),
      assignAutoresponderIsSentFrom: assign({
        autoresponder: (context, event) => {
          xlog({ label: "assignAutoresponderIsSentFrom", type: "action", context, event });
          switch (event.channel) {
            case "chat":
              return {
                ...context.autoresponder,
                isSentFromChat: !context.autoresponder.isSentFromChat,
              };
            case "email":
              return {
                ...context.autoresponder,
                isSentFromEmail: !context.autoresponder.isSentFromEmail,
              };
            case "whatsapp":
              return {
                ...context.autoresponder,
                isSentFromWhatsapp: !context.autoresponder.isSentFromWhatsapp,
              };
            case "messenger":
              return {
                ...context.autoresponder,
                isSentFromMessenger: !context.autoresponder.isSentFromMessenger,
              };
            case "allegro":
              return {
                ...context.autoresponder,
                isSentFromAllegro: !context.autoresponder.isSentFromAllegro,
              };
          }
        },
      }),
      assignAutoresponderOutsideOfWorkingHoursMessageEnabled: assign({
        autoresponder: (context, event) => {
          xlog({ label: "assignAutoresponderOutsideOfWorkingHoursMessageEnabled", type: "action", context, event });
          const { autoresponder } = context;
          const { isOutsideWorkingHoursMessageEnabled, isEnabled } = autoresponder;
          return {
            ...autoresponder,
            isEnabled: !isOutsideWorkingHoursMessageEnabled ? true : isEnabled,
            isOutsideWorkingHoursMessageEnabled: !isOutsideWorkingHoursMessageEnabled,
          };
        },
      }),
      assignAutoresponderMessage: assign({
        autoresponder: (context, event) => {
          xlog({ label: "assignAutoresponderMessage", type: "action", context, event });
          return {
            ...context.autoresponder,
            message: event.value,
          };
        },
      }),
      assignAutoresponderOutsideOfWorkingHoursMessage: assign({
        autoresponder: (context, event) => {
          xlog({ label: "assignAutoresponderOutsideOfWorkingHoursMessage", type: "action", context, event });
          return {
            ...context.autoresponder,
            outsideWorkingHoursMessage: event.value,
          };
        },
      }),
      assignWorkingHours: assign({
        workingHours: (context, event) => {
          xlog({ label: "assignWorkingHours", type: "action", context, event });
          return event.data.workingHours;
        },
      }),
      assignChatConfig: assign({
        chatConfig: (context, event) => {
          xlog({ label: "assignChatConfig", type: "action", context, event });
          return event.data.chatConfig.chatConfig;
        },
      }),
      assignWorkingHoursAfterChange: assign({
        workingHours: (context, event) => {
          xlog({ label: "assignWorkingHoursAfterChange", type: "action", context, event });
          return event.workingHours;
        },
      }),
      showIsDemoToast: (context, event) => {
        xlog({ label: "showIsDemoToast", type: "action", context, event });
        window.showToast(window._trans._inbox.inboxDemo.disabled, "info");
      },
      toastSuccess: (context, event) => {
        xlog({ label: "toastSuccess", type: "action", context, event });
        const translation = window._trans._inbox.autoresponder.toast.success;
        if (event.type === "done.invoke.editAutoresponderData") {
          window.showToast(translation.editAutoresponderData, "success");
        }
      },
      toastError: (context, event) => {
        xlog({ label: "toastError", type: "action", context, event });
        const translation = window._trans._inbox.autoresponder.toast.error;
        switch (event.type) {
          case "error.platform.editAutoresponderData":
            window.showToast(translation.editAutoresponderData, "error");
            break;
          case "error.platform.fetchAutoresponderData":
            window.showToast(translation.fetchAutoresponderData, "error");
            break;
          default:
            window.showToast(translation.default, "error");
        }
      },
      gtag: (context, event) => {
        xlog({ label: "gtag", type: "action", context, event });
        switch (event.type) {
          case "OPEN_NEW_MESSAGE_MODAL":
            gtagSend({
              event: "button_icon_click",
              component_icon_name: "plus",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "conversations",
              text: "new_message",
            });
            break;
          case "OPEN_INBOX_SETTINGS":
            gtagSend({
              event: "button_icon_click",
              component_icon_name: "gear",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "conversations",
              text: "settings",
            });
            break;
          case "CHANGE_STATUS":
            gtagSend({
              event: "navigation_tab",
              component_icon_name: "no_icon",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "conversations",
              text: `${event.status}`,
            });
            break;
          case "APPLY_FILTERS":
            gtagSend({
              event: "button_click",
              component_type: "secondary",
              component_variant: "default",
              component_icon_name: "no_icon",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "conversations",
              text: "apply_filters",
            });
            break;
          case "LOAD_MORE":
            gtagSend({
              event: "button_click",
              component_type: "secondary",
              component_variant: "default",
              component_icon_name: "no_icon",
              component_state: "active",
              component_label: "edrone",
              functionality_group: "inbox",
              functionality: "inbox",
              view: "conversations",
              text: "load_more",
            });
            break;
          default:
            break;
        }
      },
    },
    guards: {
      canLoadMore: (context, event) => {
        xlog({ label: "canLoadMore", type: "guard", context, event });
        return context.canLoadMore;
      },
      isDemo: () => {
        xlog({ label: "isDemo", type: "guard" });
        return window.EDRONE_CONTEXT.isDemo;
      },
      isUpdatedConversationOnListAndMatchesFilters: (context, event) => {
        if (!("conversationData" in event) || !event.conversationData) return false;
        xlog({ label: "isUpdatedConversationOnListAndMatchesFilters", type: "guard", context, event });
        return (
          context.conversations.some(
            (conversation) => conversation.inboxConversationId === event.conversationData.inboxConversationId
          ) &&
          checkIfConversationMatchesFiltersAndStatus(
            event.conversationData,
            context.filters,
            context.areFiltersActive,
            context.selectedStatus
          )
        );
      },
      isUpdatedConversationOnListButDoesntMatchFilters: (context, event) => {
        if (!("conversationData" in event) || !event.conversationData) return false;
        xlog({ label: "isUpdatedConversationOnListButDoesntMatchFilters", type: "guard", context, event });
        return (
          context.conversations.some(
            (conversation) => conversation.inboxConversationId === event.conversationData.inboxConversationId
          ) &&
          !checkIfConversationMatchesFiltersAndStatus(
            event.conversationData,
            context.filters,
            context.areFiltersActive,
            context.selectedStatus
          )
        );
      },
      isUpdatedConversationNotOnListButMatchesFilters: (context, event) => {
        if (!("conversationData" in event) || !event.conversationData) return false;
        xlog({ label: "isUpdatedConversationNotOnListButMatchesFilters", type: "guard", context, event });
        return (
          !context.conversations.some(
            (conversation) => conversation.inboxConversationId === event.conversationData.inboxConversationId
          ) &&
          checkIfConversationMatchesFiltersAndStatus(
            event.conversationData,
            context.filters,
            context.areFiltersActive,
            context.selectedStatus
          )
        );
      },
    },
    services: {
      fetchingConversations: async (context, event) => {
        xlog({ label: "fetchingConversations", context, event });
        const assignment = context.filters.assignment.reduce((sum, filter) => sum + Number(filter), 0);
        const source = context.filters.source.reduce((sum, filter) => sum + Number(filter), 0);
        const read = context.filters.read.reduce((sum, filter) => sum + Number(filter), 0);
        const expiration = context.filters.expiration.reduce((sum, filter) => sum + (1 << Number(filter)), 0);
        const from =
          event.type === "LOAD_MORE"
            ? context.conversations[context.conversations.length - 1].lastInboxConversationMessageId
            : 0;

        // Special handling for AI Agent tab - always filter by AI Agent source
        const isAIAgentTab = context.selectedStatus === InboxStatus.AI_AGENT;
        const effectiveStatusId = isAIAgentTab ? InboxStatus.OPEN : context.selectedStatus;
        const effectiveSourceId = isAIAgentTab ? InboxSource.AI_AGENT : source;
        // inbox_test_id: 0=PRODUCTION_ONLY (default), 1=ALL
        const testId = isAIAgentTab && context.showTestConversations ? 1 : 0;

        const response = await axios.get(
          `/inbox/get-conversations?inbox_status_id=${effectiveStatusId}` +
            (from ? `&from_id=${from}` : "") +
            (effectiveSourceId ? `&inbox_source_id=${effectiveSourceId}` : "") +
            (assignment ? `&inbox_assignment_id=${assignment}` : "") +
            (read ? `&inbox_read_id=${read}` : "") +
            (expiration ? `&inbox_expiration_id=${expiration}` : "") +
            (testId ? `&inbox_test_id=${testId}` : "") +
            (context.filters.search ? `&search=${context.filters.search}` : "")
        );
        return event.type === "LOAD_MORE" ? [...context.conversations, ...response.data.data] : response.data.data;
      },
      changeInboxSettings: async (context, event) => {
        xlog({ label: "changeInboxSettings", type: "action", context, event });
        // TODO: create one endpoint in php for saving these 3 settings
        // JIRA: https://edrone.atlassian.net/browse/PITCH-512
        if (context.inboxNotification !== event.inboxNotification) {
          await axios.post(`/inbox/update-inbox-notification?value=${event.inboxNotification}`);
        }
        if (context.inboxSendWithEnter !== event.inboxSendWithEnter) {
          await axios.post("/account/updateInboxSendWithEnter", {
            value: event.inboxSendWithEnter,
          });
        }
        return {
          inboxSendWithEnter: event.inboxSendWithEnter,
          inboxNotification: event.inboxNotification,
        };
      },
      createMessage: machineInboxNewMessage,
      fetchAutoresponderData: async (context, event) => {
        xlog({ label: "fetchAutoresponderData", type: "action", context, event });
        const [autoresponderData, workingHours, chatConfig] = await Promise.all([
          getAutoresponderData(),
          getWorkingHours(),
          getChatConfig(),
        ]);
        return { autoresponderData, workingHours, chatConfig };
      },
      editAutoresponderData: async (context, event) => {
        xlog({ label: "editAutoresponderData", context, event });
        return { autoresponderData: await editAutoresponderData(context.autoresponder) };
      },
      markAllAsRead: async (context, event) => {
        xlog({ label: "markAllAsRead", context, event });
        await axios.post("/json/inbox/markAllAsRead");
      },
    },
  }
);

function checkIfConversationMatchesFiltersAndStatus(
  conversation: Conversation,
  filters: ConversationFilters,
  areFiltersActive: boolean,
  selectedStatus: InboxStatus
) {
  // Special handling for AI Agent tab - match by source type, not status
  if (selectedStatus === InboxStatus.AI_AGENT) {
    return conversation.sourceType === "AI_AGENT";
  }
  if (!areFiltersActive && conversation.inboxConversationStatus === InboxStatus[selectedStatus]) return true;

  const matching = {
    search: false,
    read: false,
    assignment: false,
    source: false,
    status: false,
    expiration: false,
  };

  filters.search
    ? (matching.search =
        conversation.email?.includes(filters.search) || conversation.conversationPhone?.includes(filters.search))
    : (matching.search = true);

  filters.read.length
    ? (matching.read = filters.read.some(
        (filter) =>
          (filter === InboxRead.READ && conversation.read) || (filter === InboxRead.UNREAD && !conversation.read)
      ))
    : (matching.read = true);

  filters.assignment.length
    ? (matching.assignment = filters.assignment.some(
        (filter) =>
          (filter === InboxAssignment.YOU && conversation.assignedAgentId === window.EDRONE_CONTEXT.user.agentId) ||
          (filter === InboxAssignment.UNASSIGNED && !conversation.assignedAgentId)
      ))
    : (matching.assignment = true);

  filters.source.length
    ? (matching.source = filters.source.includes(InboxSource[conversation.sourceType]))
    : (matching.source = true);

  filters.expiration.length
    ? (matching.expiration = filters.expiration.some((filter) => {
        if (conversation.sourceType !== "WHATSAPP" || !conversation.expirationDate) return false;
        const now = Date.now();
        const hoursUntilExpiration = (conversation.expirationDate - now) / (1000 * 60 * 60);
        const isExpired = hoursUntilExpiration <= 0;
        const isAboutToExpire = hoursUntilExpiration > 0 && hoursUntilExpiration <= 2;
        return (
          (filter === InboxExpiration.EXPIRED && isExpired) ||
          (filter === InboxExpiration.ABOUT_TO_EXPIRE && isAboutToExpire)
        );
      }))
    : (matching.expiration = true);

  matching.status =
    conversation.inboxConversationStatus === InboxStatus[selectedStatus] ||
    (InboxStatus[selectedStatus] === "CLOSED" && conversation.inboxConversationStatus === "ARCHIVED");

  return (
    matching.search &&
    matching.read &&
    matching.assignment &&
    matching.source &&
    matching.status &&
    matching.expiration
  );
}
function sortConversationsByLastInboxMessageId(conversations: Conversation[]) {
  return conversations.sort((a, b) => b.lastInboxConversationMessageId - a.lastInboxConversationMessageId);
}

export type InboxConversationsActor = InterpreterFrom<typeof machineInboxConversations>;

// @ts-ignore
export function useInboxConversations(service) {
  return useActor(service.children.get("conversations") as InboxConversationsActor);
}
